import {
  ACTIVE,
  DONE,
  METRIC_KEYS,
  NOT_READY,
  PARTIALLY_PICKED,
  PARTIALLY_STAGED,
  PENDING_PICK,
  PENDING_STAGE,
  PICK_IN_PROGRESS,
  PICK_PENDING,
  ROUTES_AT_RISK,
  SIDELINED,
  STAGE_IN_PROGRESS,
  STAGE_PENDING,
  STAGED,
  STATUS_TO_METRIC_KEY_MAP,
  STOW_PENDING,
  STOWED,
  WORK_GROUP_TYPES,
} from "./EntityStatusManager";
import { PRODUCT_TYPE_HANDLERS, PRODUCT_TYPES } from "./ProductTypeUtils";
import groceryPickIcon from "../../assets/grocery-picklist-icon.png";
import corePickIcon from "../../assets/core-picklist-icon.png";
import React from "react";
import {
  convertMillisToSecond,
  convertSecondsToTimeFormat,
  getTimeDiffFromCurrentTimeInHHMMSSFormat,
} from "../../outboundAmzl/util/Util.js";
import { convertMillisecondsToHHMM } from "../../util/Util";
import {
  FALLBACK_DASH,
  FALLBACK_ELLIPSIS,
  ROUTE_PICKLIST_PROGRESS,
  ROUTE_SPEED,
  ROUTE_SPEED_LABELS,
  SSD,
} from "./Constants";

/**
 * Gets the progress of a picklist as a formatted string.
 * @param {Object} picklist - The picklist object containing status and progress information
 * @param {string} picklist.status - The current status of the picklist
 * @returns {string} Progress in format "completedCount/totalCount" or fallback dash
 */
export function getPicklistProgress(picklist) {
  try {
    if (!picklist) return FALLBACK_DASH;
    const progressKey = ROUTE_PICKLIST_PROGRESS[picklist.status];
    if (progressKey && picklist[progressKey]) {
      const { completedCount = 0, totalCount = 0 } = picklist[progressKey];
      return `${completedCount}/${totalCount}`;
    }
    return FALLBACK_DASH;
  } catch (error) {
    console.error("Error in getPicklistProgress:", error);
    return FALLBACK_DASH;
  }
}

/**
 * Maps picklist status to display-friendly status strings.
 * @param {Object} picklist - The picklist object
 * @param {string} picklist.status - The raw status of the picklist
 * @returns {string} Mapped status string or fallback dash
 */
export function getPicklistStatus(picklist) {
  try {
    if (!picklist || !picklist.status) return FALLBACK_DASH;

    switch (picklist.status) {
      case NOT_READY:
        return STOW_PENDING;
      case STOWED:
        return STOWED;
      case PENDING_PICK:
        return PICK_PENDING;
      case PARTIALLY_PICKED:
        return PICK_IN_PROGRESS;
      case PENDING_STAGE:
        return STAGE_PENDING;
      case PARTIALLY_STAGED:
        return STAGE_IN_PROGRESS;
      case STAGED:
        return STAGED;
      case SIDELINED:
        return SIDELINED;
      default:
        return picklist.status;
    }
  } catch (error) {
    console.error("Error in getPicklistStatus:", error);
    return FALLBACK_DASH;
  }
}

/**
 * Extracts and formats the stage time from the first cart in a picklist.
 * @param {Object} picklist - The picklist object
 * @param {Array} picklist.carts - Array of cart objects
 * @param {number} picklist.carts[].stageTime - Stage time in milliseconds
 * @returns {string} Formatted time in HH:MM format or fallback dash
 */
export function getPicklistStageTime(picklist) {
  try {
    if (
      !picklist ||
      !picklist.carts ||
      !picklist.carts[0] ||
      !picklist.carts[0].stageTime
    )
      return FALLBACK_DASH;
    return convertMillisecondsToHHMM(picklist.carts[0].stageTime);
  } catch (error) {
    console.error("Error in getPicklistStageTime:", error);
    return FALLBACK_DASH;
  }
}

/**
 * Calculates the stage duration for SSD routes based on pick associate information.
 * This method is only applicable for SSD routes.
 * @param {Object} route - The route object
 * @param {Array} route.pickAssociates - Array of pick associate objects
 * @param {number} route.pickAssociates[].pickingDuration - Duration in milliseconds
 * @param {number} route.pickAssociates[].startTime - Start time timestamp
 * @param {string} route.pickingErrorMessage - Error message if any
 * @returns {string} Formatted duration or fallback ellipsis
 */
export function calculateStageDuration(route) {
  try {
    if (
      !route ||
      !route.pickAssociates ||
      !route.pickAssociates[0] ||
      route.pickingErrorMessage
    ) {
      return FALLBACK_ELLIPSIS;
    }

    const associate = route.pickAssociates[0];
    if (associate.pickingDuration) {
      return convertSecondsToTimeFormat(
        convertMillisToSecond(associate.pickingDuration),
      );
    } else if (associate.startTime) {
      return getTimeDiffFromCurrentTimeInHHMMSSFormat(associate.startTime);
    }
    return FALLBACK_ELLIPSIS;
  } catch (error) {
    console.error("Error in calculateStageDuration:", error);
    return FALLBACK_ELLIPSIS;
  }
}
/**
 * Extracts active work group types from a picklist's work group sequence.
 * @param {Object} picklist - The picklist object
 * @param {Array} picklist.workGroupSequence - Array of work group objects
 * @param {string} picklist.workGroupSequence[].workGroupStatus - Status of the work group
 * @param {string} picklist.workGroupSequence[].workGroupType - Type of the work group
 * @returns {Array<string>} Array of active work group types
 */
export function getWorkGroupTypes(picklist) {
  try {
    if (
      !picklist ||
      !picklist.workGroupSequence ||
      !Array.isArray(picklist.workGroupSequence)
    ) {
      return [];
    }
    return picklist.workGroupSequence
      .filter((wg) => wg && wg.workGroupStatus === ACTIVE && wg.workGroupType)
      .map((wg) => wg.workGroupType);
  } catch (error) {
    console.error("Error in getWorkGroupTypes:", error);
    return [];
  }
}

/**
 * Creates a React img element for work group icons.
 * @param {string} key - Unique key for the React element
 * @param {string} src - Image source URL
 * @param {string} alt - Alt text for accessibility
 * @returns {JSX.Element} React img element with predefined styling
 */
const createIcon = (key, src, alt) => (
  <img
    key={key}
    src={src}
    alt={alt}
    style={{ width: "16px", height: "16px", marginRight: "5px" }}
  />
);

/**
 * Checks if a picklist contains any grocery work group sequences.
 * @param {Object} picklist - The picklist object
 * @param {Array} picklist.workGroupSequence - Array of work group objects
 * @returns {boolean} True if grocery work groups exist, false otherwise
 */
export function hasGroceryWorkGroupSeq(picklist) {
  if (!picklist || !picklist.workGroupSequence) return false;
  return picklist.workGroupSequence.some(
    (wg) =>
      wg &&
      wg.workGroupType &&
      wg.workGroupType.includes(WORK_GROUP_TYPES.GROCERY_PICK),
  );
}

/**
 * Checks if a picklist contains any core (non-grocery) work group sequences.
 * @param {Object} picklist - The picklist object
 * @param {Array} picklist.workGroupSequence - Array of work group objects
 * @returns {boolean} True if core work groups exist or no sequence exists, false otherwise
 */
export function hasCoreWorkGroupSeq(picklist) {
  if (!picklist || picklist.workGroupSequence == null) return true;
  return picklist.workGroupSequence.some(
    (wg) =>
      wg &&
      wg.workGroupType &&
      !wg.workGroupType.includes(WORK_GROUP_TYPES.GROCERY_PICK),
  );
}

/**
 * Renders appropriate work group icons based on picklist work group sequences.
 * @param {Object} picklist - The picklist object
 * @param {Array} picklist.workGroupSequence - Array of work group objects
 * @returns {Array<JSX.Element>} Array of React img elements representing work group icons
 */
export function renderWorkGroupIcons(picklist) {
  try {
    if (!picklist) return [];
    if (
      picklist.workGroupSequence == null ||
      getWorkGroupTypes(picklist).length === 0
    ) {
      return [createIcon("core", corePickIcon, SSD)];
    }
    const icons = new Set();
    if (hasCoreWorkGroupSeq(picklist)) {
      icons.add(createIcon("core", corePickIcon, WORK_GROUP_TYPES.CORE_PICK));
    }
    if (hasGroceryWorkGroupSeq(picklist)) {
      icons.add(
        createIcon("grocery", groceryPickIcon, WORK_GROUP_TYPES.GROCERY_PICK),
      );
    }
    return Array.from(icons);
  } catch (error) {
    console.error("Error in renderWorkGroupIcons:", error);
    return [];
  }
}

/**
 * Applies product type filtering to determine if a route matches the filter criteria.
 * @param {Object} viewModelF - The view model filter object
 * @param {string} viewModelF.productType - The product type to filter by
 * @param {Object} route - The route object to filter
 * @param {Array} route.picklists - Array of picklist objects in the route
 * @returns {boolean} True if route matches filter criteria, false otherwise
 */
export function applyProductTypeFilter(viewModelF, route) {
  try {
    if (
      !viewModelF ||
      !viewModelF.productType ||
      !route ||
      !route.picklists ||
      !route.picklists.length
    ) {
      return viewModelF && viewModelF.productType === PRODUCT_TYPES.ALL_ROUTES;
    }

    const handler = PRODUCT_TYPE_HANDLERS[viewModelF.productType];
    return handler && handler.typeChecker
      ? route.picklists.some(handler.typeChecker)
      : true;
  } catch (error) {
    console.error("Error in applyProductTypeFilter:", error);
    return false;
  }
}

/**
 * Generates status metrics for a route based on product type.
 * @param {Object} route - The route object
 * @param {string} productType - The product type to generate metrics for
 * @returns {Object} Object containing boolean flags for various route status metrics
 */
export function getRouteStatusMetrics(route, productType) {
  try {
    const metrics = {
      isAtRisk: false,
      totalGrocery: false,
      stowPending: false,
      stowed: false,
      pickPending: false,
      pickInProgress: false,
      stagePending: false,
      stageInProgress: false,
      staged: false,
      notStarted: false,
      progressing: false,
      done: false,
    };

    if (!route) return metrics;

    const handler = PRODUCT_TYPE_HANDLERS[productType];
    return processRouteStatusMetrics(
      route,
      metrics,
      handler.typeChecker,
      handler.allowedStatuses,
      `getRouteStatusMetrics-${productType}`,
    );
  } catch (error) {
    console.error("Error in getRouteStatusMetrics:", error);
    return {};
  }
}

/**
 * Updates metrics object based on status and risk assessment.
 * @param {Object} metrics - The metrics object to update
 * @param {string} status - The current status
 * @param {Array<string>} allowedStatuses - Array of allowed status values
 * @param {boolean} [isAtRisk=false] - Whether the route is at risk
 */
function updateMetricsForStatus(
  metrics,
  status,
  allowedStatuses,
  isAtRisk = false,
) {
  if (allowedStatuses.includes(status)) {
    const metricKey = STATUS_TO_METRIC_KEY_MAP[status];
    if (metricKey) metrics[metricKey] = true;
    if (isAtRisk && allowedStatuses.includes(ROUTES_AT_RISK)) {
      metrics[METRIC_KEYS.AT_RISK_STATUS] = true;
    }
  }
}

/**
 * Checks if a route's critical pull time is breached.
 * @param {Object} route - The route object
 * @returns {boolean} True if route critical time is breached, false otherwise
 */
function hasAtRiskRoutes(route) {
  return (
    route.routeStatus !== DONE &&
    isStageByTimeBreached(route.criticalPullTime, route.routeStatus)
  );
}

/**
 * Checks if any picklists in a route are at risk based on timing constraints.
 * @param {Object} route - The route object
 * @param {Function} [typeChecker] - Optional function to check specific picklist types
 * @returns {boolean} True if any picklists are at risk, false otherwise
 */
export function hasAtRiskPicklists(route, typeChecker) {
  return (
    route.picklists &&
    route.picklists.some((picklist) => {
      return (
        picklist.status !== STAGED &&
        (!typeChecker || typeChecker(picklist)) &&
        isStageByTimeBreached(picklist.stageByTime, picklist.status)
      );
    })
  );
}

/**
 * Determines if a route is at risk based on timing constraints.
 * @param {Object} route - The route object
 * @param {Function} [typeChecker] - Optional function to check specific picklist types
 * @returns {boolean} True if route is at risk, false otherwise
 */
export function isRouteAtRisk(route, typeChecker) {
  if (typeChecker) {
    return hasAtRiskPicklists(route, typeChecker);
  }
  return hasAtRiskRoutes(route) || hasAtRiskPicklists(route);
}

/**
 * Processes and updates status metrics for a route.
 * @param {Object} route - The route object
 * @param {Object} metrics - The metrics object to update
 * @param {Function} [typeChecker] - Optional function to check specific picklist types
 * @param {Array<string>} allowedStatuses - Array of allowed status values
 * @param {string} errorContext - Context string for error logging
 * @returns {Object} Updated metrics object
 */
function processRouteStatusMetrics(
  route,
  metrics,
  typeChecker,
  allowedStatuses,
  errorContext,
) {
  try {
    if (!route || !metrics) return metrics;
    if (typeChecker && (!route.picklists || !route.picklists.length))
      return metrics;

    const status = typeChecker
      ? route.picklists.find(typeChecker) &&
        route.picklists.find(typeChecker).status
      : route.routeStatus;

    const atRisk = isRouteAtRisk(route, typeChecker);
    updateMetricsForStatus(metrics, status, allowedStatuses, atRisk);
    return metrics;
  } catch (error) {
    console.error(`Error in ${errorContext}:`, error);
    return metrics;
  }
}

/**
 * Gets the location information for a picklist based on its status.
 * Returns staging locations for staged picklists, cart IDs for others.
 * @param {Object} picklist - The picklist object
 * @param {string} picklist.status - The status of the picklist
 * @param {Array} picklist.carts - Array of cart objects
 * @param {string} picklist.carts[].stagingLocation - Staging location for staged carts
 * @param {string} picklist.carts[].cartId - Cart identifier
 * @returns {string} Comma-separated location string or fallback dash
 */
export function getPicklistLocation(picklist) {
  try {
    if (!picklist || !picklist.carts || picklist.carts.length === 0)
      return FALLBACK_DASH;

    const showStagingLocation = [STAGED, STAGE_IN_PROGRESS].includes(
      picklist.status,
    );
    const locations = new Set();

    picklist.carts.forEach((cart) => {
      if (showStagingLocation) {
        if (cart.stagingLocation && cart.stagingLocation.label) {
          locations.add(cart.stagingLocation.label);
        }
      } else {
        if (cart.cartId) {
          locations.add(cart.cartId);
        }
      }
    });

    return locations.size > 0
      ? Array.from(locations).join(", ")
      : FALLBACK_DASH;
  } catch (error) {
    console.error("Error in getStagingLocation:", error);
    return FALLBACK_DASH;
  }
}
/**
 * Checks if the stage-by time has been breached (current time exceeds stage-by time).
 * @param {number} stageByTime - The target stage-by timestamp in milliseconds
 * @param {string} [status] - The status of the item (if SIDELINED, breach check is skipped)
 * @returns {boolean} True if stage-by time is breached, false otherwise
 */
export function isStageByTimeBreached(stageByTime, status) {
  try {
    if (!stageByTime || status === SIDELINED) return false;
    return Date.now() > stageByTime;
  } catch (error) {
    console.error("Error in isStageByTimeBreached:", error);
    return false;
  }
}

const ROUTE_SPEED_MAP = {
  [ROUTE_SPEED.RUSH_1_HR]: ROUTE_SPEED_LABELS.ONE_HOUR,
  [ROUTE_SPEED.RUSH_3_HR]: ROUTE_SPEED_LABELS.THREE_HOUR,
  [ROUTE_SPEED.STANDARD]: ROUTE_SPEED_LABELS.STANDARD_SPEED,
};

/**
 * Renders a  route speed label from route speed code.
 * @param {string} routeSpeed - The route speed code
 * @returns {string}  route speed label or fallback dash
 */
export const renderRouteSpeed = (routeSpeed) => {
  try {
    return ROUTE_SPEED_MAP[routeSpeed] || FALLBACK_DASH;
  } catch (error) {
    console.error("Error in renderRouteSpeed:", error);
    return FALLBACK_DASH;
  }
};



// WEBPACK FOOTER //
// ./src/components/RouteStatus/utils/Util.js